// Deck as an installed app: the service worker (public/static/sw.js) with its 「有新版本 · 刷新」 toast, and 「安装到桌面」 in
// 「⚙ 设置」 (Chrome/Edge/Android: the browser's install prompt; iOS has none, so it says how: 分享 → 添加到主屏幕),
// plus a one-time hint. app.js calls initPwa() first thing, before it waits for anything.
import { icon } from '/icons.js';
import { t, tr } from '/i18n.js';
const HINT = 'deck.installHint';
const standalone = () => matchMedia('(display-mode: standalone), (display-mode: window-controls-overlay)').matches || navigator.standalone === true;
const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
let deferred = null; // the browser's beforeinstallprompt, kept until 「安装到桌面」

const st = document.createElement('style');
st.textContent = `.pwatoast{position:fixed;left:50%;transform:translateX(-50%);top:calc(64px + env(safe-area-inset-top));z-index:70;display:flex;gap:10px;align-items:center;background:#fff8e7;border:3px solid #1b1410;border-radius:999px;padding:6px 8px 6px 14px;font:13px/1.5 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410;box-shadow:0 3px 0 #1b1410;white-space:nowrap;max-width:calc(100vw - 24px)}
.pwatoast button{font:inherit;border:2px solid #1b1410;border-radius:999px;padding:2px 12px;background:#FDD03B;cursor:pointer}
.pwatoast button.x{background:#fff;padding:2px 8px}
#settings .pwasteps{margin:4px 0 0 18px;padding:0;font-size:13px}`;
document.head.append(st);

/** A small pill under the top bar: text, an action button, and ✕ (text, action: catalog keys). */
function pill(id, text, action, go, ms) {
  document.getElementById(id)?.remove();
  const d = document.createElement('div'); d.id = id; d.className = 'pwatoast'; d.setAttribute('role', 'status');
  const s = tr(document.createElement('span'), text);
  const b = tr(document.createElement('button'), action); b.type = 'button'; b.onclick = () => { d.remove(); go(); };
  const x = document.createElement('button'); x.type = 'button'; x.className = 'x'; x.append(icon('close')); tr(x, null, null, { 'aria-label': 'pwa.close' }); x.onclick = () => d.remove();
  d.append(s, b, x); document.body.append(d);
  if (ms) setTimeout(() => d.remove(), ms);
}

async function install() {
  if (!deferred) return;
  const e = deferred; deferred = null;
  e.prompt();
  try { await e.userChoice; } catch {}
}

function iosSteps(box) {
  const ol = document.createElement('ol'); ol.className = 'pwasteps';
  for (const [s, i] of [['pwa.install.ios.step1'], ['pwa.install.ios.step2', 'upload'], ['pwa.install.ios.step3']]) { const li = document.createElement('li'); li.textContent = t(s); if (i) li.append(' ', icon(i)); ol.append(li); }
  box.append(ol);
}

function registerWorker() {
  if (!('serviceWorker' in navigator)) return;
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => { if (refreshing) location.reload(); });
  // The page's own app.js (index.html is network-first, so online it's already this deploy's)
  const mine = document.querySelector('script[type=module][src^="/assets/app-"], script[type=module][src^="/app.js"]')?.getAttribute('src') ?? ''; // (built: /assets/app-<hash>.js)
  const onLanding = () => { const j = document.getElementById('join'); return !!j && j.style.display !== 'none'; };
  /** Does the waiting worker belong to the deploy this page already runs? (it lists this page's app.js) */
  const sameDeploy = (w) => new Promise((ok) => {
    const ch = new MessageChannel(); const t = setTimeout(() => ok(false), 1500);
    ch.port1.onmessage = (e) => { clearTimeout(t); ok(Array.isArray(e.data?.stamped) && e.data.stamped.includes(mine)); };
    w.postMessage({ t: 'stamped' }, [ch.port2]);
  });
  const offer = async (reg) => {
    // only an update (a page already controlled by the old worker); the very first install needs no reload
    const w = reg.waiting;
    if (!w || !navigator.serviceWorker.controller) return;
    // Already running this version (the usual case: index.html came from the network): just let the new worker take
    // over, no reload and no toast. Still on the 上船 card: reload now, before anyone boards, rather than ask later.
    if (await sameDeploy(w)) { w.postMessage({ t: 'skipWaiting' }); return; }
    if (onLanding()) { refreshing = true; w.postMessage({ t: 'skipWaiting' }); return; }
    pill('pwaUpdate', 'pwa.update.text', 'pwa.update.action', () => { refreshing = true; reg.waiting?.postMessage({ t: 'skipWaiting' }); });
  };
  navigator.serviceWorker.register('/sw.js', { scope: '/' }).then((reg) => {
    offer(reg);
    reg.addEventListener('updatefound', () => {
      const w = reg.installing;
      w?.addEventListener('statechange', () => { if (w.state === 'installed') offer(reg); });
    });
    // a Deck left open for days (installed apps often are): look for a new version when it comes back, and hourly
    const check = () => reg.update().catch(() => {});
    check(); // at once, so a new deploy is picked up while still on the 上船 card
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
    setInterval(check, 60 * 60 * 1000);
  }).catch((e) => console.warn('service worker not registered', e));
}

/** The tab title and the iOS home-screen name follow this deployment's manifest (deck-dev: 「Raft Deck Dev」, served by
 *  the worker, src/routes/manifest.ts), so an installed dev app can't be mistaken for production. */
function appName() {
  fetch('/manifest.webmanifest').then((r) => r.json()).then((m) => {
    if (!m?.name || m.name === 'Raft Deck') return;
    document.title = document.title.replace('Raft Deck', m.name);
    document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute('content', m.short_name || m.name);
  }).catch(() => {});
}

export function initPwa({ addSettingsSection }) {
  registerWorker();
  appName();
  addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferred = e; });
  addEventListener('appinstalled', () => { deferred = null; document.getElementById('pwaHint')?.remove(); try { localStorage.setItem(HINT, '1'); } catch {} });
  addSettingsSection({
    id: 'install', title: () => t('pwa.install.title'), order: 900,
    render(box) {
      const p = (k) => { const e = document.createElement('p'); e.textContent = t(k); box.append(e); };
      if (standalone()) return p('pwa.install.done');
      if (deferred) {
        p('pwa.install.why');
        const b = document.createElement('button'); b.type = 'button'; b.textContent = t('pwa.install.button');
        b.style.cssText = 'font:inherit;border:2px solid #1b1410;border-radius:999px;padding:3px 14px;background:#FDD03B;cursor:pointer';
        b.onclick = () => install().then(() => b.closest('.setbody') && (b.disabled = true));
        return box.append(b);
      }
      if (ios) { p('pwa.install.ios.intro'); return iosSteps(box); }
      p('pwa.install.other');
    },
  });
  // the one-time hint, a little after the page settles (and only where installing is possible)
  setTimeout(() => {
    let seen = false; try { seen = !!localStorage.getItem(HINT); } catch {}
    if (seen || standalone() || !(deferred || ios)) return;
    try { localStorage.setItem(HINT, '1'); } catch {}
    if (deferred) pill('pwaHint', 'pwa.hint.install', 'pwa.hint.installAction', install, 12000);
    else if (!/iPhone|iPod/.test(navigator.userAgent)) pill('pwaHint', 'pwa.hint.ios', 'pwa.hint.ok', () => {}, 12000); // an iPhone gets public/fullscreen.js's card instead
  }, 15000);
}
